import { assertNever } from "./assert-never";
import "./App.scss";
import { CircularProgress } from "@mui/material";
import { newAccount } from "./components/Login";
import { Suspense, useEffect, useState } from "react";
import * as borogove from "borogove";
import { isTauri } from "@tauri-apps/api/core";
import { getCurrentWindow } from "@tauri-apps/api/window";
import { onAction } from "@tauri-apps/plugin-notification";
import { borogove_persistence_Sqlite as borogoveSqlite } from "borogove/sqlite-wasm";
import * as Sentry from "@sentry/browser";
import { stem } from "porter2";
import { Await, useAsyncValue, Outlet, useParams } from "react-router";
import { ios, webkit, useEffectEvent, serviceWorker } from "./util";
import {
	showMessageNotification,
	useNotifications,
	NotificationData,
} from "./notifications";
import { SettingsContext, SettingsData } from "./components/Settings";
import { Call } from "./components/Call";
import { LoginRequest } from "./components/LoginRequest";
import { mediaStore } from "./media-store";

Sentry.init({
	dsn: "https://ec9ae5aef68e4035937d4c5943c4156c@app.glitchtip.com/7125",

	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration(),
	],

	tracesSampleRate: 0.0,
	tracePropagationTargets: [],

	replaysSessionSampleRate: 0.0,
	replaysOnErrorSampleRate: 1.0,
});

const idbp = await borogove.persistence.IDB("snikket", mediaStore, null, stem);
let persistence = null;
if (typeof navigator.storage?.getDirectory === "function" && (webkit || ios)) {
	persistence = new borogoveSqlite("snikket.sqlite3", mediaStore);
	mediaStore.setKV(idbp); // Use IDB for media KV for now because we can't use opfs-sahpool from service worker
} else {
	persistence = idbp;
}

export type Accounts = Record<string, ReturnType<typeof newAccount>>;

const chatForNotification = (data: NotificationData, accounts: Accounts) => {
	if (data.type === "loginRequest") return null;
	return accounts[data.accountId]?.client.getChat(data.chatId) ?? null;
};

export type Context = {
	accounts: Accounts;
	setAccounts: (
		accounts: Accounts | ((accounts: Accounts) => Accounts),
	) => void;
	persistence: borogove.Persistence & borogove.persistence.KeyValueStore;
	idbp: Awaited<ReturnType<typeof borogove.persistence.IDB>>;
	initialSelectedAccount: string | null;
	setSettings: (settings: SettingsData) => void;
};

const AppInner = () => {
	const { initialAccounts, initialSelectedAccount, initialSettings } =
		useAsyncValue() as {
			initialAccounts: Accounts;
			initialSelectedAccount: string;
			initialSettings: SettingsData;
		};
	const [accounts, setAccounts] = useState(initialAccounts);
	const [settings, setSettings] = useState<SettingsData>(initialSettings);
	const params = useParams();
	const { showNotification, onNotificationClick, onNotificationClose } =
		useNotifications();

	const notificationEvent = useEffectEvent(
		(
			client: borogove.Client,
			message: borogove.ChatMessage,
			event: borogove.ChatMessageEvent,
		) => {
			(async () => {
				if (event !== borogove.ChatMessageEvent.DeliveryEvent) return;
				if (!message.isIncoming()) return;
				if (message.type === borogove.MessageType.MessageCall) return;
				const chat = client.getChat(message.chatId());
				if (!chat) return;

				if (
					!document.hidden &&
					document.hasFocus() &&
					message.chatId() === params.chatId
				)
					return;
				if (message.versions.length > 0) return; // No notify for edits

				const members = await chat.getMemberDetails(
					[message.senderId].concat(
						message.replyToMessage ? [message.replyToMessage.senderId] : [],
					),
				);
				if (
					!chat.notificationsFiltered() ||
					(chat.notifyMention() &&
						message.body().toPlainText().indexOf(client.displayName()) >= 0) ||
					(chat.notifyReply() && message.replyToMessage && members[1]?.isSelf)
				) {
					if (settings.audibleNotifications) new Audio("/message.opus").play();
					await showMessageNotification(
						client.accountId(),
						members[0] ?? message.senderMemberStub(),
						message,
						chat,
						showNotification,
					);
				}
			})();
		},
	);

	useEffect(() => {
		for (const [, account] of Object.entries(accounts)) {
			account.client.preferE2ee(
				settings.e2ee === "pfs"
					? borogove.OutgoingE2EEPreference.OMEMO
					: borogove.OutgoingE2EEPreference.NoE2EE,
			);
		}
	}, [accounts, settings.e2ee]);

	useEffect(() => {
		window.accounts = Object.freeze(accounts);

		const tokens = Object.fromEntries(
			Object.keys(accounts).map((accountId) => {
				const client = accounts[accountId].client;
				const setStatus = (status: string) =>
					setAccounts({
						...accounts,
						[accountId]: { ...accounts[accountId], status },
					});
				return [
					accountId,
					[
						client.addStatusOnlineListener(() => setStatus("online")),
						client.addStatusOfflineListener(() => setStatus("connecting")),
						client.addConnectionFailedListener(() => setStatus("error")),
						client.addPasswordNeededListener(() => setStatus("auth")),
						client.addChatMessageListener((message, event) =>
							notificationEvent(client, message, event),
						),
					],
				];
			}),
		);

		return () => {
			for (const [accountId, ts] of Object.entries(tokens)) {
				ts.map((t) => accounts[accountId].client.removeEventListener(t));
			}
		};
	}, [accounts]);

	useEffect(() => {
		if (!serviceWorker) return;
		const handler = (
			event: MessageEvent<{
				event: "notificationclick" | "notificationclose";
				data: NotificationData;
			}>,
		) => {
			const { event: action, data } = event.data;
			const chat = chatForNotification(data, accounts);
			switch (action) {
				case "notificationclick":
					onNotificationClick(data, chat);
					break;
				case "notificationclose":
					onNotificationClose(data, chat);
					break;
				default:
					return assertNever(action);
			}
		};
		serviceWorker.addEventListener("message", handler);

		return () => serviceWorker?.removeEventListener("message", handler);
	}, [accounts, onNotificationClick, onNotificationClose]);

	useEffect(() => {
		if (!isTauri()) return;

		const handler = onAction(async ({ actionId, notification }) => {
			if (actionId !== "tap" || !notification) return;

			const data = notification.extra as NotificationData;
			const chat = chatForNotification(data, accounts);

			onNotificationClick(data, chat);
			const appWindow = getCurrentWindow();
			await appWindow.show();
			await appWindow.unminimize();
			await appWindow.setFocus();
		});

		return () => void handler.then((handler) => handler.unregister());
	}, [accounts, onNotificationClick]);

	return (
		<SettingsContext.Provider value={settings}>
			<Outlet
				context={{
					accounts,
					setAccounts,
					persistence,
					idbp,
					initialSelectedAccount,
					setSettings,
				}}
			/>
			<Call accounts={accounts} />
			<LoginRequest accounts={accounts} />
		</SettingsContext.Provider>
	);
};

const initialP = (async () => {
	const accountIds = await persistence.listAccounts();
	const initialSelectedAccount =
		(await persistence.get("ui#selectedAccount")) || accountIds[0];
	return {
		initialAccounts: Object.fromEntries(
			accountIds.map((id: string) => [id, newAccount(id, idbp, persistence)]),
		),
		initialSelectedAccount,
		initialSettings: {
			enterIsSend:
				"" + (await persistence.get("config#enterIsSend")) !== "false",
			richTextToolbar:
				"" + (await persistence.get("config#richTextToolbar")) === "true",
			audibleNotifications:
				"" + (await persistence.get("config#audibleNotifications")) === "true",
			messageRequests:
				(await persistence.get("config#messageRequests")) ?? "spam",
			e2ee: (await persistence.get("config#e2ee")) ?? "none",
		},
	};
})();

export const App = () => {
	return (
		<Suspense fallback={<CircularProgress className="fullscreen" />}>
			<Await resolve={initialP}>
				<AppInner />
			</Await>
		</Suspense>
	);
};
